import { useCallback, useEffect, useRef, useState } from "react";
import { moderationApi, ModerationApiError, type ReasonCategory } from "../../lib/moderation-api";
import { reviewApi, ReviewApiError, type TerritoryInfo } from "../../lib/review-api";
import type { PublicSnapshot } from "../../lib/publication-api";
import { PublicationSnapshotView } from "../PublicationSnapshotView";
import { OwnerStateBadge, deriveOwnerState } from "./reviewShared";
import { fmtDate, shortId } from "./adminShared";

/**
 * RIGHT pane — Territory Inspector.
 *
 * Renders the REAL published content through the canonical
 * `PublicationSnapshotView`, fed by the admin-authorized snapshot (which can see
 * moderated/hidden publications — the single server-side moderation bypass).
 * Mark Reviewed is revision-locked: a stale command (owner republished while the
 * admin inspected) is rejected server-side and surfaced as a NEWER REVISION
 * warning with "load latest". Moderate / Emergency Hide / Restore operate from
 * here so it is a real control room, not a read-only viewer.
 */
export function TerritoryInspector({
  identityId,
  reasons,
  onMutated,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}: {
  identityId: string | null;
  reasons: ReasonCategory[];
  onMutated: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}) {
  const [info, setInfo] = useState<TerritoryInfo | null>(null);
  const [snapshot, setSnapshot] = useState<PublicSnapshot | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const reqRef = useRef(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    setStale(false);
    setActionErr(null);
    if (!identityId) {
      setInfo(null);
      setSnapshot(null);
      setLoadErr(null);
      return;
    }
    const my = ++reqRef.current;
    const c = new AbortController();
    setInfo(null);
    setSnapshot(null);
    setLoadErr(null);
    reviewApi
      .territory(identityId, c.signal)
      .then(async (ti) => {
        if (my !== reqRef.current) return;
        setInfo(ti);
        if (ti.has_active_publication) {
          const snap = await reviewApi.snapshot(identityId, c.signal).catch(() => null);
          if (my === reqRef.current) setSnapshot(snap);
        } else {
          setSnapshot(null);
        }
      })
      .catch((e) => {
        if (c.signal.aborted || my !== reqRef.current) return;
        setLoadErr(e instanceof ReviewApiError ? e.message : "Failed to load Territory");
      });
    return () => c.abort();
  }, [identityId, reloadKey]);

  if (!identityId) {
    return (
      <div data-testid="review-inspector-empty" className="panel-chamfer panel-chamfer--sm p-8 text-center font-mono text-[12px] text-text-muted">
        Select a Territory from the queue or a client to inspect its publication, revisions and review state.
      </div>
    );
  }
  if (loadErr) {
    return (
      <div data-testid="review-inspector-error" className="panel-chamfer panel-chamfer--sm p-6 font-mono text-[12px] text-state-danger">
        {loadErr}
      </div>
    );
  }
  if (!info) {
    return (
      <div data-testid="review-inspector-loading" className="panel-chamfer panel-chamfer--sm p-8 text-center font-mono text-[12px] text-text-muted">
        Loading Territory…
      </div>
    );
  }

  const state = deriveOwnerState(info);
  const review = info.review;
  const moderated = info.moderation.moderated;
  const published = info.has_active_publication;
  const currentRev = review?.current_publication_revision ?? snapshot?.publication_revision ?? 0;
  const alreadyReviewed = !!review?.reviewed;

  const markReviewed = async () => {
    if (!review) return;
    setBusy(true);
    setActionErr(null);
    try {
      await reviewApi.markReviewed(info.identity_id, review.current_publication_revision);
      setStale(false);
      reload();
      onMutated();
    } catch (e) {
      if (e instanceof ReviewApiError && (e.code === "REVIEW_STALE" || e.code === "REVIEW_CONFLICT")) {
        setStale(true);
      } else {
        setActionErr(e instanceof ReviewApiError ? e.message : "Mark Reviewed failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  const loadLatest = () => {
    setStale(false);
    reload();
    onMutated();
  };

  return (
    <div data-testid="review-inspector" data-identity-id={info.identity_id} className="panel-chamfer panel-chamfer--sm">
      <div className="chamfer-strip">
        <span className="strip-dot" aria-hidden />
        <span>Inspector</span>
        <span className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            data-testid="review-inspector-prev"
            onClick={onPrev}
            disabled={!hasPrev}
            className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-border-soft text-text-secondary hover:text-text-primary disabled:opacity-30"
            aria-label="Previous"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden><path d="M8.5 3.5L5 7l3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <button
            type="button"
            data-testid="review-inspector-next"
            onClick={onNext}
            disabled={!hasNext}
            className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-border-soft text-text-secondary hover:text-text-primary disabled:opacity-30"
            aria-label="Next"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden><path d="M5.5 3.5L9 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </span>
      </div>

      <div className="p-4 space-y-4 max-h-[68vh] overflow-y-auto scroll-dark">
        {/* Header / state */}
        <div className="flex flex-wrap items-center gap-2">
          <OwnerStateBadge state={state} testid="review-owner-state" />
          <span className="ml-auto font-mono text-[9px] text-text-muted">{fmtDate(info.review?.current_published_at)}</span>
        </div>

        <dl className="space-y-1.5">
          <KV label="Name"><span className="text-text-primary">{info.display_name ?? "—"}</span></KV>
          <KV label="Owner"><span className="font-mono text-[11px] text-text-secondary break-all">{info.owner ?? "—"}</span></KV>
          <KV label="Identity"><span className="font-mono text-[10px] text-text-muted break-all">{info.identity_id}</span></KV>
          {published ? (
            <>
              <KV label="Current rev">
                <span data-testid="review-current-revision" className="font-mono text-[11px] text-text-primary tabular-nums">{currentRev}</span>
              </KV>
              <KV label="Last reviewed">
                <span data-testid="review-last-reviewed" className="font-mono text-[11px] text-text-secondary tabular-nums">
                  {review?.last_reviewed_revision ?? "—"}
                </span>
                {review?.reviewed_by && <span className="ml-2 font-mono text-[9px] text-text-muted break-all">{shortId(review.reviewed_by, 8, 6)}</span>}
              </KV>
            </>
          ) : (
            <>
              <KV label="Parcels">
                <span data-testid="review-parcel-count" className="font-mono text-[11px] text-text-secondary tabular-nums">{info.parcel_count}</span>
                {info.member_parcel_ids.length > 0 && (
                  <span className="ml-2 font-mono text-[9px] text-text-muted">#{info.member_parcel_ids.slice(0, 8).join(", ")}</span>
                )}
              </KV>
              {info.footprint && (
                <KV label="Footprint">
                  <span className="font-mono text-[10px] text-text-muted tabular-nums">
                    {info.footprint.bbox.w}×{info.footprint.bbox.h} · {info.footprint.area} u²
                  </span>
                </KV>
              )}
            </>
          )}
        </dl>

        {published ? (
          <>
            {/* Stale CAS warning */}
            {stale && (
              <div data-testid="review-stale-warning" className="rounded-md border px-3 py-2.5" style={{ borderColor: "rgba(240,180,41,0.5)", background: "rgba(240,180,41,0.08)" }}>
                <div className="font-mono text-[10px] uppercase tracking-[0.14em]" style={{ color: "#f0b429" }}>Newer revision live</div>
                <p className="mt-1 text-[11.5px] text-text-secondary leading-relaxed">
                  This Territory was republished while you were inspecting it. Your Mark Reviewed was rejected so an older revision can never be blessed. Load the latest revision before reviewing.
                </p>
                <button
                  type="button"
                  data-testid="review-load-latest"
                  onClick={loadLatest}
                  className="mt-2 inline-flex items-center h-7 px-3.5 rounded-md font-mono text-[10px] uppercase tracking-[0.16em] text-text-primary neon-glass pill-ghost"
                >
                  Load latest
                </button>
              </div>
            )}

            {/* Mark Reviewed */}
            <div className="rounded-md border border-border-soft bg-obsidian/40 p-3">
              {alreadyReviewed ? (
                <div data-testid="review-reviewed-state" className="font-mono text-[11px]" style={{ color: "var(--state-success, #58d68d)" }}>
                  Reviewed at revision {review?.last_reviewed_revision}. Nothing pending.
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    data-testid="review-mark-reviewed"
                    onClick={markReviewed}
                    disabled={busy || !review}
                    className="facet-btn facet-btn--active h-7 px-3.5 text-[10px] tracking-[0.16em] disabled:opacity-40"
                  >
                    {busy ? "Working…" : `Mark Reviewed (rev ${currentRev})`}
                  </button>
                  <span className="font-mono text-[9.5px] text-text-muted">Revision-locked · post-publication</span>
                </div>
              )}
              {actionErr && <div data-testid="review-action-error" className="mt-2 font-mono text-[11px] text-state-danger">{actionErr}</div>}
            </div>

            {/* Moderation controls */}
            <ModerationControls info={info} reasons={reasons} onMutated={() => { reload(); onMutated(); }} setBusy={setBusy} busy={busy} />

            {/* Rendered publication (canonical PublicPublicationView path) */}
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted">Publication</span>
                {moderated && (
                  <span data-testid="review-hidden-label" className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-neon-coral border border-[color:var(--neon-coral-ring)] rounded-sm px-1.5 py-0.5">
                    Hidden · Moderation Active
                  </span>
                )}
              </div>
              <div
                data-testid="review-inspector-publication"
                className="rounded-md border border-border-soft bg-obsidian/40 overflow-hidden"
                style={moderated ? { boxShadow: "inset 0 0 0 1px var(--neon-coral-ring)" } : undefined}
              >
                {snapshot ? (
                  <PublicationSnapshotView snapshot={snapshot} />
                ) : (
                  <div data-testid="review-inspector-no-publication" className="p-6 font-mono text-[12px] text-text-muted">
                    No active publication for this Territory.
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div data-testid="review-unpublished" className="rounded-md border border-border-soft bg-obsidian/40 p-6 text-center">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted">Publication</div>
            <div data-testid="review-unpublished-title" className="mt-2 font-display text-[15px] text-text-secondary">No Published Content</div>
            <p className="mt-2 text-[11.5px] text-text-muted leading-relaxed">
              This owner holds the Territory but has never published. Nothing is public and there is nothing to review yet — it enters the Review Queue the moment it is published.
            </p>
            {info.footprint && (
              <div className="mt-3 font-mono text-[10px] text-text-muted tabular-nums">
                Footprint {info.footprint.bbox.w}×{info.footprint.bbox.h} · {info.footprint.parcel_count} parcels · {info.footprint.area} u²
              </div>
            )}
            <div className="mt-3 flex items-center justify-center gap-4 font-mono text-[9px] uppercase tracking-[0.16em] text-text-muted">
              <span>Publication: <span className="text-text-secondary">None</span></span>
              <span>Review: <span className="text-text-secondary">N/A</span></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ModerationControls({
  info,
  reasons,
  onMutated,
  setBusy,
  busy,
}: {
  info: TerritoryInfo;
  reasons: ReasonCategory[];
  onMutated: () => void;
  setBusy: (b: boolean) => void;
  busy: boolean;
}) {
  const [mode, setMode] = useState<"hide" | "emergency" | "restore" | null>(null);
  const [reason, setReason] = useState(reasons[0]?.key ?? "");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const moderated = info.moderation.moderated;

  useEffect(() => {
    setMode(null);
    setNote("");
    setErr(null);
  }, [info.identity_id]);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (mode === "restore") {
        if (!info.moderation.case_id) throw new Error("No active case.");
        await moderationApi.restore(info.moderation.case_id, note.trim() || undefined);
      } else if (mode === "hide" || mode === "emergency") {
        if (!reason) { setErr("Select a reason category."); setBusy(false); return; }
        if (!note.trim()) { setErr("An Admin Note is required."); setBusy(false); return; }
        await moderationApi.hide({
          target_type: "territory",
          target_id: info.identity_id,
          reason_category: reason,
          admin_note: note.trim(),
          emergency: mode === "emergency",
        });
      }
      setMode(null);
      setNote("");
      onMutated();
    } catch (e) {
      setErr(e instanceof ModerationApiError ? `${e.code}: ${e.message}` : e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="review-moderation-controls" className="rounded-md border border-border-soft bg-obsidian/40 p-3">
      <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted">Moderation</div>
      {moderated ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-neon-coral">
            {info.moderation.status ?? "Moderated"}{info.moderation.emergency ? " · emergency" : ""}
          </span>
          <button
            type="button"
            data-testid="review-restore"
            data-active={mode === "restore"}
            onClick={() => { setMode(mode === "restore" ? null : "restore"); setErr(null); }}
            className="ml-auto inline-flex items-center h-7 px-3.5 rounded-md font-mono text-[10px] uppercase tracking-[0.16em] text-state-success border border-[color:rgba(88,214,141,0.4)] hover:bg-[color:rgba(88,214,141,0.1)] transition-colors"
          >
            Restore
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="review-moderate"
            data-active={mode === "hide"}
            onClick={() => { setMode(mode === "hide" ? null : "hide"); setErr(null); }}
            className="inline-flex items-center h-7 px-3.5 rounded-md font-mono text-[10px] uppercase tracking-[0.16em] text-neon-coral border border-[color:var(--neon-coral-ring)] hover:bg-[color:var(--neon-coral-soft)] transition-colors"
          >
            Moderate
          </button>
          <button
            type="button"
            data-testid="review-emergency-hide"
            data-active={mode === "emergency"}
            onClick={() => { setMode(mode === "emergency" ? null : "emergency"); setErr(null); }}
            className="inline-flex items-center h-7 px-3.5 rounded-md font-mono text-[10px] uppercase tracking-[0.16em] text-neon-coral border border-[color:var(--neon-coral-ring)] hover:bg-[color:var(--neon-coral-soft)] transition-colors"
          >
            Emergency Hide
          </button>
        </div>
      )}

      {mode && (
        <div className="mt-3 space-y-2">
          {(mode === "hide" || mode === "emergency") && (
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.16em] text-text-muted mb-1">Reason</label>
              <select
                data-testid="review-mod-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full h-8 rounded-md border border-border-soft bg-obsidian px-2 font-mono text-[11px] text-text-primary focus:outline-none focus:border-border-accent"
              >
                {reasons.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.16em] text-text-muted mb-1">
              {mode === "restore" ? "Note (optional)" : "Admin Note (required)"}
            </label>
            <textarea
              data-testid="review-mod-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full resize-none rounded-md border border-border-soft bg-obsidian px-2.5 py-2 text-[12px] text-text-primary focus:outline-none focus:border-border-accent"
            />
          </div>
          {err && <div data-testid="review-mod-error" className="font-mono text-[11px] text-state-danger">{err}</div>}
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="review-mod-confirm"
              onClick={run}
              disabled={busy}
              className="inline-flex items-center h-7 px-4 rounded-md font-mono text-[10px] uppercase tracking-[0.16em] text-text-primary neon-glass pill-ghost disabled:opacity-40"
            >
              {busy ? "Working…" : mode === "restore" ? "Confirm Restore" : mode === "emergency" ? "Confirm Emergency Hide" : "Confirm Hide"}
            </button>
            <button
              type="button"
              data-testid="review-mod-cancel"
              onClick={() => { setMode(null); setNote(""); setErr(null); }}
              className="inline-flex items-center h-7 px-3 rounded-md font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted border border-border-soft hover:text-text-primary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="w-24 shrink-0 font-mono text-[8.5px] uppercase tracking-[0.18em] text-text-muted pt-0.5">{label}</dt>
      <dd className="m-0 flex-1 text-[12px]">{children}</dd>
    </div>
  );
}
